import { useCallback, useEffect, useRef } from 'react';

/**
 * Find the ancestor that actually scrolls.
 *
 * The overflow style matters, not just the measurements: a conversation sits
 * inside several nested flex wrappers, and `scrollHeight > clientHeight` is true
 * of `overflow: hidden` containers too. Latching onto one of those and setting
 * `scrollTop` does nothing visible — the feed silently stays where it was.
 */
function findScroller(from: HTMLElement | null): HTMLElement | null {
  let el = from?.parentElement ?? null;
  while (el) {
    const overflowY = window.getComputedStyle(el).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
}

const SETTLE_MS = 400;
const MAX_MS = 3000;
/** How close to the bottom still counts as "at the bottom". */
const PINNED_SLACK_PX = 24;
/** How long to keep looking for the feed's scroller while the thread loads. */
const ATTACH_MAX_MS = 10000;

/**
 * Open a conversation at the newest message.
 *
 * Place the returned ref on an empty element at the end of the feed. Whenever
 * `key` changes — a different conversation — this pins the feed to the bottom
 * and keeps it there while the content settles, then stops.
 *
 * The settling matters: a thread's height changes several times after it
 * mounts. The fetch resolves, bubbles lay out, an email card or a recording
 * player expands. A single scroll on mount, or a couple of fixed timeouts,
 * lands on whatever height happened to exist at that instant. Instead this
 * re-pins on every height change until the height has held still for 400ms,
 * giving up after 3s so a feed that never settles can't spin forever.
 *
 * It deliberately stops once settled, so it never fights someone scrolling up
 * to read back through the conversation — and it stops early if they start
 * scrolling (wheel, touch, keys) while it is still settling.
 *
 * Pass `ready = false` while the content for `key` is still being fetched.
 * Without it the settle window runs against the empty placeholder: that holds
 * still for 400ms, the hook stops, and the events land afterwards with the
 * feed parked at the top. Only `key` and `ready` restart it, and a refetch
 * keeps its data (so `ready` stays true): new events never drag someone who
 * scrolled up back down.
 */
export function useScrollToBottom(key: unknown, ready = true) {
  const endRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    const scroller = findScroller(endRef.current);
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, []);

  useEffect(() => {
    if (!ready) return;

    let raf = 0;
    let lastHeight = -1;
    let settledAt = 0;
    let stopped = false;
    let watched: HTMLElement | null = null;
    const startedAt = performance.now();

    // The reader took over — leave the scroll position to them.
    const stop = () => {
      stopped = true;
      cancelAnimationFrame(raf);
    };
    const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

    const tick = () => {
      if (stopped) return;
      const now = performance.now();
      const scroller = findScroller(endRef.current);

      if (scroller && scroller !== watched) {
        USER_SCROLL_EVENTS.forEach((t) => watched?.removeEventListener(t, stop));
        USER_SCROLL_EVENTS.forEach((t) =>
          scroller.addEventListener(t, stop, { passive: true }),
        );
        watched = scroller;
      }

      if (scroller) {
        if (scroller.scrollHeight !== lastHeight) {
          lastHeight = scroller.scrollHeight;
          settledAt = now;
          scroller.scrollTop = scroller.scrollHeight;
        } else if (now - settledAt > SETTLE_MS) {
          return; // height has held still — we're at the bottom, stop.
        }
      }

      if (now - startedAt < MAX_MS) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      USER_SCROLL_EVENTS.forEach((t) => watched?.removeEventListener(t, stop));
    };
  }, [key, ready]);

  // Stay at the bottom when the feed's viewport shrinks. The composer under the
  // feed grows as someone types a long message, which takes height from the
  // feed; scrollTop alone would leave the newest messages hidden under it. Only
  // when the reader was already at the bottom — someone scrolled up to read
  // back keeps their place. The end marker may not be mounted yet while the
  // thread loads, so look for the scroller each frame until it appears.
  useEffect(() => {
    if (!ready || typeof ResizeObserver === 'undefined') return;
    let scroller: HTMLElement | null = null;
    let observer: ResizeObserver | null = null;
    let atBottom = true;
    let raf = 0;
    const startedAt = performance.now();
    const onScroll = () => {
      if (scroller) atBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= PINNED_SLACK_PX;
    };
    const attach = () => {
      scroller = findScroller(endRef.current);
      if (!scroller) {
        if (performance.now() - startedAt < ATTACH_MAX_MS) raf = requestAnimationFrame(attach);
        return;
      }
      scroller.addEventListener('scroll', onScroll, { passive: true });
      observer = new ResizeObserver(() => {
        if (scroller && atBottom) scroller.scrollTop = scroller.scrollHeight;
      });
      observer.observe(scroller);
    };
    raf = requestAnimationFrame(attach);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      scroller?.removeEventListener('scroll', onScroll);
    };
  }, [key, ready]);

  return { endRef, scrollToBottom };
}
